import { PHASE, PLAYER, PLAYER_STATS_TABLES } from "../../../common/constants.ts";
import { idb } from "../db/index.ts";
import { g, helpers } from "../util/index.ts";
import type {
	UpdateEvents,
	ViewInput,
	Player,
	PlayerStatType,
} from "../../../common/types.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";
import { getBestPos } from "../core/player/checkJerseyNumberRetirement.ts";
import { getActivePlayoffTids } from "./playerRatings.ts";
import { getUserTeamGamesPlayed } from "./userTeamGamesPlayed.ts";

const updatePlayers = async (
	inputs: ViewInput<"playerStats">,
	updateEvents: UpdateEvents,
	state: any,
) => {
	if (
		updateEvents.includes("firstRun") ||
		(inputs.season === g.get("season") && updateEvents.includes("gameSim")) ||
		updateEvents.includes("playerMovement") ||
		inputs.abbrev !== state.abbrev ||
		inputs.season !== state.season ||
		inputs.statType !== state.statType ||
		inputs.playoffs !== state.playoffs
	) {
		let statsTable;
		if (inputs.statType === "advanced") {
			statsTable = PLAYER_STATS_TABLES.advanced;
		} else if (inputs.statType === "shotLocations") {
			statsTable = PLAYER_STATS_TABLES.shotLocations;
		} else if (inputs.statType === "gameHighs") {
			statsTable = PLAYER_STATS_TABLES.gameHighs;
		} else {
			statsTable = PLAYER_STATS_TABLES.regular;
		}

		// eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
		if (!statsTable) {
			throw new Error(`Invalid statType: "${inputs.statType}"`);
		}

		const stats = statsTable.stats;
		let actualStats;
		if (inputs.season === "career") {
			actualStats = [
				...stats,

				// Used in processPlayersHallOfFame
				"ewa",
			];
		} else {
			actualStats = stats;
		}

		let playersAll: Player[];
		if (g.get("season") === inputs.season && g.get("phase") <= PHASE.PLAYOFFS) {
			playersAll = await idb.cache.players.indexGetAll("playersByTid", [
				PLAYER.FREE_AGENT,
				Infinity,
			]);
		} else {
			playersAll = await idb.getCopies.players(
				{
					activeSeason:
						typeof inputs.season === "number" ? inputs.season : undefined,
				},
				"noCopyCache",
			);
		}

		let tid: number | undefined = g
			.get("teamInfoCache")
			.findIndex((t) => t.abbrev === inputs.abbrev);

		if (tid < 0) {
			tid = undefined;
		}

		let statType: PlayerStatType;
		if (inputs.statType === "totals") {
			statType = "totals";
		} else if (inputs.statType === "per36") {
			statType = "per36";
		} else {
			statType = "perGame";
		}

		if (tid === undefined) {
			if (inputs.abbrev === "watch") {
				playersAll = playersAll.filter((p) => p.watch);
			} else if (inputs.abbrev === "playoffs") {
				const playoffTids = await getActivePlayoffTids();
				playersAll = playersAll.filter((p) => playoffTids.has(p.tid));
			}
		}

		const getPlayersPlus = async (showNoStats = false) =>
			await idb.getCopies.playersPlus(playersAll, {
				attrs: [
					"pid",
					"firstName",
					"lastName",
					"age",
					"born",
					"ageAtDeath",
					"injury",
					"tid",
					"abbrev",
					"hof",
					"watch",
					"awards",
				],
				ratings: ["skills", "pos", "season"],
				stats: ["abbrev", "tid", "jerseyNumber", "season", ...actualStats],
				season: typeof inputs.season === "number" ? inputs.season : undefined,
				tid,
				statType,
				playoffs: inputs.playoffs === "playoffs",
				regularSeason: inputs.playoffs === "regularSeason",
				combined: inputs.playoffs === "combined",
				mergeStats: "totOnly",
				showNoStats,
				showRookies: showNoStats,
			});

		const expandAllSeasons = (players: any[]) => {
			if (inputs.season !== "all") {
				return players;
			}

			return players.flatMap((p) =>
				p.stats.map((ps: any) => {
					const ratings =
						p.ratings.find((pr: any) => pr.season === ps.season) ??
						p.ratings.at(-1);

					return {
						...p,
						ratings,
						stats: ps,
					};
				}),
			);
		};

		const filterPlayedRows = (players: any[], keepNoStatsRows = false) => {
			if (inputs.abbrev === "watch") {
				return players;
			}

			return players.filter((p) => {
				if (inputs.season !== "career") {
					return keepNoStatsRows || p.stats.gp > 0;
				} else if (inputs.playoffs === "playoffs") {
					return p.careerStatsPlayoffs.gp > 0;
				} else if (inputs.playoffs === "combined") {
					return p.careerStatsCombined.gp > 0;
				} else {
					return p.careerStats.gp > 0;
				}
			});
		};

		let players = filterPlayedRows(expandAllSeasons(await getPlayersPlus()));

		const allowCurrentSeasonNoStatsRows =
			typeof inputs.season === "number" &&
			inputs.season === g.get("season") &&
			inputs.playoffs !== "playoffs";
		if (players.length === 0 && allowCurrentSeasonNoStatsRows) {
			if (tid !== undefined) {
				playersAll = await idb.getCopies.players({ tid }, "noCopyCache");
			}

			players = filterPlayedRows(
				expandAllSeasons(await getPlayersPlus(true)),
				true,
			);

			if (tid !== undefined) {
				for (const p of players) {
					p.stats.tid = p.tid;
					p.stats.abbrev = p.abbrev;
				}
			}
		}

		players = addFirstNameShort(players);

		for (const p of players) {
			if (inputs.season === "career") {
				p.pos = getBestPos(p, tid);
			} else if (Array.isArray(p.ratings) && p.ratings.length > 0) {
				p.pos = p.ratings.at(-1).pos;
			} else if (p.ratings.pos !== undefined) {
				p.pos = p.ratings.pos;
			} else {
				p.pos = "?";
			}
		}

		let superCols;
		if (inputs.season === "all") {
			if (statsTable.superCols) {
				// Account for extra "Season" column
				superCols = helpers.deepCopy(statsTable.superCols);
				if (superCols[0]) {
					superCols[0].colspan += 1;
				}
			}
		} else {
			superCols = statsTable.superCols;
		}

		// A3 stat-meaningfulness gate input; undefined for historical scopes
		const userTeamGamesPlayed = await getUserTeamGamesPlayed(
			inputs.season,
			inputs.playoffs,
		);

		return {
			players,
			abbrev: inputs.abbrev,
			season: inputs.season,
			statType: inputs.statType,
			playoffs: inputs.playoffs,
			stats,
			superCols,
			userTeamGamesPlayed,
			userTid: g.get("userTid"),
		};
	}
};

export default updatePlayers;
